import { useCallback, useState } from 'react';
import { getApiErrorMessage } from '@/api/errors';
import { useLatest } from './useLatest';

/**
 * Outcome of an action run. Never throws - callers branch on `ok`.
 */
type ActionResult<R> = { ok: true; value: R } | { ok: false; error: string };

export interface UseAsyncActionOptions<Args extends unknown[]> {
  /**
   * Message used when the server does not provide one
   * (a function receives the action arguments)
   */
  errorMessage: string | ((...args: Args) => string);
  /**
   * Derive a busy-state key from the arguments (e.g. the row id) so several
   * rows can track their own pending state. Defaults to a single shared key.
   */
  getKey?: (...args: Args) => string;
  /**
   * Called with the user-facing message when the action fails
   */
  onError?: (message: string) => void;
}

export interface UseAsyncActionResult<Args extends unknown[], R> {
  /** Run the action; resolves with a result object instead of throwing */
  run: (...args: Args) => Promise<ActionResult<R>>;
  /** Whether the action is pending for `key` (or for the default key) */
  isPending: (key?: string) => boolean;
  /** Whether any invocation is pending */
  isAnyPending: boolean;
  /** Message of the last failure (cleared when a run starts) */
  error: string | null;
  clearError: () => void;
}

const DEFAULT_KEY = '__default__';

/**
 * Wraps an async operation with per-key busy state and normalised errors.
 *
 * Replaces the copy-pasted `setXingId(id); try { await api.x(id) } catch
 * { setError(err.message) } finally { setXingId(null) }` handlers.
 *
 * @example
 * ```tsx
 * const renew = useAsyncAction((cert: Certificate) => renewCertificate(cert.id), {
 *   errorMessage: 'Failed to renew certificate',
 *   getKey: (cert) => cert.id,
 * });
 * <button disabled={renew.isPending(cert.id)} onClick={() => renew.run(cert)} />
 * ```
 */
export function useAsyncAction<Args extends unknown[], R>(
  action: (...args: Args) => Promise<R>,
  options: UseAsyncActionOptions<Args>
): UseAsyncActionResult<Args, R> {
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  const actionRef = useLatest(action);
  const optionsRef = useLatest(options);

  const run = useCallback(
    async (...args: Args): Promise<ActionResult<R>> => {
      const { getKey, errorMessage, onError } = optionsRef.current;
      const key = getKey ? getKey(...args) : DEFAULT_KEY;

      setError(null);
      setPendingKeys((prev) => new Set(prev).add(key));

      try {
        const value = await actionRef.current(...args);
        return { ok: true, value };
      } catch (err) {
        const fallback = typeof errorMessage === 'function' ? errorMessage(...args) : errorMessage;
        const message = getApiErrorMessage(err, fallback);
        setError(message);
        onError?.(message);
        return { ok: false, error: message };
      } finally {
        setPendingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [actionRef, optionsRef]
  );

  const isPending = useCallback(
    (key: string = DEFAULT_KEY) => pendingKeys.has(key),
    [pendingKeys]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    run,
    isPending,
    isAnyPending: pendingKeys.size > 0,
    error,
    clearError,
  };
}
